import React, {
  useEffect,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import {
  CircularProgress,
  colors,
  Container,
  Fab,
  Hidden,
  LabelDisplayedRowsArgs,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  Add as IconAddNew,
  GroupOutlined as IconEmpty
} from '@material-ui/icons';

import { SplashScreen } from '@checkit/react-components';

import { FloatButton } from '../../common/FloatButton';
import TablePaginationActions from '../../common/TablePaginationActions';
import { useListState } from '../../common/useListState';
import MaterialUiLink from '../../components/MaterialUiLink';
import { useAxiosDispatch } from '../../middleware/axios';
import { setRowsPerPage } from '../../preferences/actions';
import { State } from '../../state';
import { appMessages } from '../../translations';
import {
  clearRecentlyModified,
  getUserDefinedRoles
} from '../actions';
import { UserDefinedRole } from '../state';

import { UserDefinedRoleActions } from './UserDefinedRoleActions';
import { messages } from './UserDefinedRoleList.translations';

const useStyles = makeStyles((theme: Theme) => ({
  active: {
    backgroundColor: colors.green['50'],
    color: colors.green['800']
  },
  highlight: {
    backgroundColor: colors.yellow['200']
  },
  inactive: {
    backgroundColor: colors.grey['200'],
    color: colors.grey['800']
  },
  roleStatus: {
    borderRadius: '5px',
    display: 'inherit',
    padding: theme.spacing(1),
    textAlign: 'center'
  },
  spacer: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  tableCell: {
    whiteSpace: 'pre-wrap'
  },
  tableRoot: {
    borderColor: theme.palette.divider,
    borderTop: 'solid 1px'
  },
  toolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  }
}));

interface UserDefinedRoleRowProps {
  /** The user-defined role which this row corresponds to. */
  role: UserDefinedRole;
  /** Whether this row should be highlighted (if the role was recently modified). */
  isHighlighted?: boolean;
}
function UserDefinedRoleRow({ role, isHighlighted = false }: UserDefinedRoleRowProps) {
  const { formatMessage } = useIntl( );
  const classes = useStyles( );

  const activeText = role.isActive
    ? formatMessage(messages['userRoles.list.active'])
    : formatMessage(messages['userRoles.list.inactive']);
  // TODO: will need updated contents when merged with CSD-12017
  const activeCellInner = (<span>{activeText}</span>);
  const activeCell = role.isActive
    ? activeCellInner
    : (<Tooltip title={formatMessage(messages['userRoles.list.inactive.tooltip'])}>{activeCellInner}</Tooltip>);

  return (
    <TableRow key={role.code} className={isHighlighted ? classes.highlight : ''}>
      <TableCell className={classes.tableCell}>{role.name}</TableCell>
      <Hidden mdDown={true}>
        <TableCell className={classes.tableCell}>{role.description}</TableCell>
        <TableCell className={classes.tableCell}>
          <div className={classes.roleStatus + ' ' + (role.isActive ? classes.active : classes.inactive)}>
           {activeCell}
          </div>
        </TableCell>
      </Hidden>
      <TableCell>
        <UserDefinedRoleActions role={role} />
      </TableCell>
    </TableRow>
  );
}

export function UserDefinedRolesListImpl({
  userDefinedRoles,
  preferences,
  location
}: {
  /** All fetched user-defined roles, from global state. */
  userDefinedRoles: State['userDefinedRoles'];
  /** User preferences, from global state. */
  preferences: State['preferences'];
  location: RouteComponentProps['location'];
}) {
  const { formatMessage } = useIntl( );
  const dispatch = useAxiosDispatch( );
  const classes = useStyles( );
  const theme = useTheme( );
  const showRowsPerPage = useMediaQuery(theme.breakpoints.up('md'));

  // State

  const { rowsPerPage } = preferences;
  const totalCount = userDefinedRoles.allCodes.length;

  const [ page, setPage ] = useState(0);
  // whether the list items have been loaded at least once
  const [ hasBeenLoaded, setHasBeenLoaded ] = useState(false);
  const state = useListState({
    clearRecentlyModified,
    getItemsAction: getUserDefinedRoles,

    maxPage: Math.ceil(totalCount / rowsPerPage),
    page,
    setPage
  });

  useEffect(function updateHasBeenLoaded( ) {
    if (state === 'loaded') {
      setHasBeenLoaded(true);
    }
  }, [ state ]);

  // Methods

  function getLabelForNumberOfDisplayedRows({ from, to, count }: LabelDisplayedRowsArgs) {
    const message = messages['userRoles.list.displayedRows'];
    return formatMessage(message, { from, to, count });
  }
  function handleChangePage(_: unknown, newPage: number) {
    dispatch(clearRecentlyModified());
    setPage(newPage);
  }
  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    setPage(0);
  }

  // Rendering

  const rowIndex = rowsPerPage * page;
  const codesForThisPage = userDefinedRoles.allCodes.slice(rowIndex, rowIndex + rowsPerPage);
  const codesRecentlyModified = userDefinedRoles.recentlyModified;
  // we're always going to be showing codesRecentlyModified at the top, so avoid showing a role twice
  // if it's in that array but also in codesForThisPage
  const codesDeduplicated = codesForThisPage.filter((code) => !codesRecentlyModified.includes(code));
  // actual number of roles shown on this page
  const numRoles = codesDeduplicated.length + codesRecentlyModified.length;

  const isLoadingInitially = state === 'loading' && !hasBeenLoaded;
  // show a full-page loading spinner if the list hasn't been loaded yet
  // avoids the list showing, then being removed to show the splash screen
  if (isLoadingInitially) {
    return (
      <Container maxWidth="sm" style={{ textAlign: 'center' }}>
        <CircularProgress color="inherit" />
      </Container>
    );
  }
  // show a splash screen prompting the user to create a new library if they have none in their account
  const isEmptyAccount = state === 'loaded' && totalCount === 0;
  if (isEmptyAccount) {
    return (
      <SplashScreen
        icon={IconEmpty}
        heading={formatMessage(messages['userRoles.list.empty.primary'])}
        details={formatMessage(messages['userRoles.list.empty.secondary'])}
      >
        <Container style={{ textAlign: 'center' }}>
          <Fab
            color="primary"
            variant="extended"
            style={{ marginTop: '1em' }}
            component={MaterialUiLink}
            to={{ pathname: '/user-defined-roles/new', state: { previousLocation: location } }}
          >
            <IconAddNew style={{ marginRight: '0.3em' }} />
            <span>{formatMessage(messages['userRoles.list.empty.action'])}</span>
          </Fab>
        </Container>
      </SplashScreen>
    );
  }

  const createRowWith = (props: Omit<UserDefinedRoleRowProps, 'role'>) => function createRowFor(code: string) {
    const role = userDefinedRoles.byCode[code];
    if (role == null) {
      throw new Error(`User-defined role missing from state: "${code}"`);
    }
    return (<UserDefinedRoleRow key={code} role={role} {...props} />);
  };
  const tableBody = state !== 'loaded' ? (
    <TableRow>
      <TableCell align="center" colSpan={5}>
        <CircularProgress color="inherit" />
      </TableCell>
    </TableRow>
  ) : numRoles === 0 ? (
    <TableRow>
      <TableCell align="center" colSpan={6}>
        {formatMessage(messages['userRoles.list.empty.primary'])}
      </TableCell>
    </TableRow>
  ) : [
    ...codesRecentlyModified.map(createRowWith({ isHighlighted: true })),
    ...codesDeduplicated.map(createRowWith({ isHighlighted: false }))
  ];

  return (
    <>
      <Paper>
        <Table classes={{ root: classes.tableRoot }}>
          <TableHead>
            <TableRow>
              <TableCell>{formatMessage(messages['userRoles.list.name'])}</TableCell>
              <Hidden mdDown={true}>
                <TableCell>{formatMessage(messages['userRoles.list.description'])}</TableCell>
                <TableCell>{formatMessage(messages['userRoles.list.status'])}</TableCell>
              </Hidden>
              <TableCell>{formatMessage(messages['userRoles.list.actions'])}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>{tableBody}</TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                labelRowsPerPage={formatMessage(messages['userRoles.list.rowsPerPage'])}
                labelDisplayedRows={getLabelForNumberOfDisplayedRows}

                classes={{ spacer: classes.spacer, toolbar: classes.toolbar }}
                count={totalCount || 0}
                page={page}

                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={showRowsPerPage ? [5, 10, 20] : []}

                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}

                ActionsComponent={TablePaginationActions}
                SelectProps={{
                  inputProps: { 'aria-label': formatMessage(messages['userRoles.list.rowsPerPage']) },
                  native: true
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>

      <FloatButton
        label={formatMessage(appMessages['app.add'])}
        innerText={formatMessage(appMessages['app.add'])}
        to={{ pathname: '/user-defined-roles/new', state: { previousLocation: location } }}
      />
    </>
  );
}
export const UserDefinedRolesList = connect(
  ({ preferences, userDefinedRoles }: State) => ({ preferences, userDefinedRoles })
)(UserDefinedRolesListImpl);
